<template>
	<div
		class="upgrade-box"
		:class="{
			'upgrade-box--premium upgrade-box--light':
				!isEventType && isPremium,
			'upgrade-box--light': skin === 'light',
			'upgrade-box--dark': skin === 'dark' && !isPremium,
			'upgrade-box--event': skin === 'event' && !isPremium,
			'upgrade-box--event-premium': skin === 'event' && isPremium,
		}"
		:data-upgrade-id="id"
		:data-event-type="eventType"
		@click="handleClick"
	>
		<div
			class="upgrade-box-container"
			:class="{ 'upgrade-box-container--row': hasRedirect }"
		>
			<div class="sub" :class="`sub--${context}`">
				<div class="upgrade-box__heading">
					<BoxHeaderBackground
						slug="upgrade-box-header"
						:gradient="headerGradient"
					/>
					<ShrinkText
						:containerClassList="
							hasFlag
								? 'upgrade-box__heading__wrapper'
								: 'upgrade-box__heading__wrapper upgrade-box__heading__wrapper__fw'
						"
						:textClassList="'upgrade-box__text'"
						:text="computedHeading"
						:fontSize="rerenderFontSize"
						:key="rerenderFontSize"
						animatedOverflow
						:min-font-size="10"
						@rerender="rerender"
					/>
					<RibbonLabel v-if="isNew" />
					<div v-if="isStarred" class="flag-with-star-icon" />
				</div>
				<div
					class="upgrade-box__content"
					:class="{
						'upgrade-box__content--has-flag': hasFlag,
						'upgrade-box__content--has-price': price,
					}"
				>
					<div v-if="hasIcon" :class="`icon-upgrade-${id}-38`" />
					<span class="description">
						{{ descriptionText }}
					</span>
				</div>
				<UpgradeFlag
					v-if="hasFlag"
					:flagVariant="flag"
					:currentLevel="currentLevel ?? 0"
					:maxLevel="maxLevel ?? 0"
					:isEventType="isEventType"
				/>
				<div v-if="price" class="upgrade-box__price">
					<div class="maxed-out" v-if="currentLevel === maxLevel">
						<span>{{ translate("ui.maxed_out") }}</span>
					</div>
					<div class="amount" v-else>
						<span class="price-label">
							{{ translate("ui.price") }}:
						</span>
						<template
							v-for="(value, key, index) in price"
							:key="key"
						>
							<AppPrice
								:amount="value ?? 0"
								:currency="key as ResourceType"
							/>
							<span
								class="amount--or"
								v-if="index < Object.keys(price).length - 1"
								>{{ translate("ui.or") }}</span
							>
						</template>
					</div>
				</div>
			</div>
			<div class="upgrade-box__redirect" v-if="hasRedirect">
				<div class="arrow arrow--right-18" />
			</div>
		</div>
		<ToastMessage
			class="upgrade-message"
			:show-message="!!toastMessage"
			:translated-text="toastMessage ?? ''"
			:has-animation="true"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import RibbonLabel from "@/components/RibbonLabel.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import BoxHeaderBackground from "@/components/SVG/BoxHeaderBackground.vue";
import ToastMessage from "@/components/ToastMessage.vue";
import UpgradeFlag from "@/components/Upgrades/UpgradeFlag.vue";
import { ResourceType } from "@/game/entities/config/resources";
import type { PriceValue, UpgradeId } from "@/game/entities/config/upgrades";
import SoundService from "@/game/SoundService";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useSegmentManager from "@/hooks/useSegmentManager";
import router from "@/router";
import type { PopupAction } from "@/store/usePopupStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import useSettingsStore from "@/store/useSettingsStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import type { UIContexts } from "@/types/ui";
import { EventCategory, RouteNames, SkinTypes, StatusTypes } from "@/types/ui";
import { EventFlagVariant, FlagVariant } from "@/types/upgrades";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { canTranslate, translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

interface Props {
	id?: UpgradeId;
	context?: UIContexts;
	heading: string | Array<string>;
	description: string;
	descriptionReplace?: { [key: string]: string };
	// replaceValue: string | number | null;
	price?: PriceValue;
	currentLevel?: number;
	maxLevel?: number;
	isNew?: boolean;
	isStarred?: boolean;
	skin?: SkinTypes;
	hasRedirect?: boolean;
	disableClick?: boolean;
	hasIcon?: boolean;
	isEventType?: boolean;
	upgradeCallback?: () => void;
	eventType?: EventCategory;
}

const {
	id = null,
	context = null,
	heading,
	description = "...",
	descriptionReplace = {},
	// replaceValue = null,
	price = null,
	currentLevel = null,
	maxLevel = null,
	isNew = false,
	isStarred = false,
	skin = SkinTypes.DARK,
	hasRedirect = false,
	disableClick = false,
	hasIcon = true, // IMPORTANT: do not change (true is default)
	isEventType = false,
	eventType,
	upgradeCallback = null,
} = defineProps<Props>();

const {
	query: { id: queryId },
} = useRoute() as { query: { id?: string } };

const rerenderFontSize = ref(14);
const rerender = (newFontSize: number): void => {
	rerenderFontSize.value = newFontSize;
};
const toastMessage = ref<string | null>(null);

const emitter = markRaw(useEmitter());
const soundService = markRaw(SoundService.getInstance());

const popupStore = usePopupStore();
const settingsStore = useSettingsStore();
const { showConfirmGemsMessagesUpgrades } = storeToRefs(settingsStore);

const { gems, coins, eventium } = storeToRefs(useResourcesStore());

const computedHeading = computed((): string => {
	let result = "";
	if (typeof heading === "string") {
		result = translate(heading);
	} else {
		result = heading.map((part: string) => translate(part)).join(" ");
	}
	return result;
});

const isPremium = computed((): boolean => {
	if (!price || Object.keys(price).length > 1) return false;
	return "gems" in price;
});

const descriptionText = computed((): string => {
	let text = translate(description);
	Object.entries(descriptionReplace).forEach(
		([key, value]: [string, string]) => {
			const textTranslated = canTranslate(value)
				? translate(value)
				: value;
			text = replacePlaceholder(text, key, textTranslated);
		},
	);
	return text;
});

const upgradesStore = useUpgradesStore();

const lowGems = computed((): boolean => {
	const gemsPrice = price?.gems;

	if (gemsPrice) {
		return gemsPrice > gems.value;
	}

	return false;
});

const lowCoins = computed((): boolean => {
	const coinsPrice = price?.coins;

	if (coinsPrice) {
		return coinsPrice > coins.value;
	}

	return false;
});

const lowEventium = computed((): boolean => {
	const eventiumPrice = price?.eventium;

	if (eventiumPrice) {
		return eventiumPrice > eventium.value;
	}

	return false;
});

const headerGradient = computed(
	(): { stopColor1: string; stopColor2: string } | null => {
		if (isEventType && eventType === EventCategory.SOLAR) {
			return {
				stopColor1: "#0D6C27",
				stopColor2: "#0D6C27",
			};
		}

		return null;
	},
);
const handleToastMessage = (key = ""): void => {
	if (lowCoins.value && (!key || (key && key === ResourceType.COINS))) {
		handleMessage("ui.not_enough_coins");
		return;
	}

	// Iba ako fallback, aj tak by mal presmerovat do obchodu
	if (lowGems.value && (!key || (key && key === ResourceType.GEMS))) {
		handleMessage("ui.not_enough_gems");
		return;
	}

	if (lowEventium.value && (!key || (key && key === ResourceType.EVENTIUM))) {
		handleMessage("event.not_enough_resources");
		return;
	}

	handleMessage("ui.upgrade_successful");
};

const handleClick = (): void => {
	if (disableClick) return;
	if (hasRedirect) {
		emitter.emit(EmitterEvents.ToggleLevelUpPopup, false);
		soundService.playGenerickClick();
		router.push({
			name: RouteNames.UPGRADES,
			query: { id },
		});
		return;
	}
	if (!id) return;

	let headingString = "";
	if (Array.isArray(heading)) {
		heading.forEach((item: string): void => {
			headingString += translate(item) + " ";
		});
	} else {
		headingString = translate(heading);
	}
	if (price && Object.keys(price).length > 1) {
		const actions: Array<PopupAction> = [];
		// fill action from price
		(Object.entries(price) as Array<[ResourceType, number]>)
			.slice()
			.forEach(([key, value]: [ResourceType, number]): void => {
				actions.push({
					label: "",
					buttonType:
						key === ResourceType.GEMS
							? StatusTypes.SECONDARY
							: StatusTypes.DEFAULT,
					price: { type: key, value },
					callback: async (): Promise<void> => {
						if (key === ResourceType.GEMS && lowGems.value) {
							router.push({
								name: RouteNames.SHOP,
							});
							popupStore.hidePopup();
							return;
						}
						handleToastMessage(key);

						const nextLvl =
							upgradesStore.getCurrentLevelById(id) + 1;
						if (
							(maxLevel ?? 0) >
							upgradesStore.getCurrentLevelById(id)
						) {
							upgradesStore.increaseCurrentLevelByIdAndPrice(
								id,
								key,
							);
							if (
								nextLvl ===
									upgradesStore.getCurrentLevelById(id) &&
								upgradeCallback
							) {
								upgradeCallback();
							}
							popupStore.hidePopup();
							soundService.playUpgradeClaim();

							const sm = useSegmentManager();
							sm.markAllSegmentsAsDirty();
							sm.run(true); // nech to lagne pri kupe a nie pri ticku
						} else {
							soundService.playUpdateMaxLevel();
						}
						popupStore.hidePopup();
					},
				});
			});
		popupStore.showPopup({
			id: PopupDictionary.UPGRADE_PRICE_SELECTION,
			type: PopupType.UPGRADE,
			heading: translate("ui.price_selection"),
			body: headingString,
			skin: SkinTypes.LIGHT,
			actions,
			maxHeight: 13,
			isCentered: true,
			isPaddingless: true,
		});
		return;
	}
	if (lowGems.value) {
		router.push({
			name: RouteNames.SHOP,
		});
		return;
	}
	if (isPremium.value && showConfirmGemsMessagesUpgrades.value) {
		popupStore.showPopup({
			id: PopupDictionary.PREMIUM_UPGRADE,
			type: PopupType.INFO,
			heading: translate("ui.premium_upgrade"),
			body: {
				ctaText: translate("ui.upgrade_confirm_message"),
				ctaText2: headingString,
				price: { gems: price?.gems ?? 0 },
			},
			skin: SkinTypes.PREMIUM,
			showSkipMessageNotice: true,
			actions: [
				{
					label: translate("ui.upgrade"),
					buttonType: StatusTypes.PRIMARY,
					callback: async (): Promise<void> => {
						tryBuyUpgrade(id);

						popupStore.hidePopup();
					},
				},
				{
					label: translate("button.cancel"),
					buttonType: StatusTypes.DEFAULT,
					callback: async (): Promise<void> => {
						popupStore.hidePopup();
					},
				},
			],
			maxHeight: 20,
			isCentered: true,
			isPaddingless: true,
		});
		return;
	}
	tryBuyUpgrade(id);
};

const tryBuyUpgrade = (id: number): void => {
	const nextLvl = upgradesStore.getCurrentLevelById(id) + 1;
	if ((maxLevel ?? 0) > upgradesStore.getCurrentLevelById(id)) {
		handleToastMessage();

		upgradesStore.increaseCurrentLevelById(id);
		if (
			nextLvl === upgradesStore.getCurrentLevelById(id) &&
			upgradeCallback
		) {
			upgradeCallback();
		}
		soundService.playUpgradeClaim();

		const sm = useSegmentManager();
		sm.markAllSegmentsAsDirty();
		sm.run(true); // nech to lagne pri kupe a nie pri ticku
	} else {
		soundService.playUpdateMaxLevel();
	}
};

const hasFlag = computed((): boolean => {
	return !hasRedirect && currentLevel !== null && maxLevel !== null;
});

const flag = computed((): FlagVariant | EventFlagVariant => {
	if ((currentLevel ?? 0) >= (maxLevel ?? 0)) {
		if (isEventType) {
			return EventFlagVariant.BLUE;
		}
		return FlagVariant.BLUE;
	}
	if (isPremium.value) {
		if (isEventType) {
			return EventFlagVariant.ORANGE;
		}

		return FlagVariant.PURPLE;
	}
	return isEventType ? EventFlagVariant.PURPLE : FlagVariant.GREEN;
});

onMounted((): void => {
	if (!queryId || +queryId !== id) return;

	const element = document.querySelector(
		`.upgrade-box[data-upgrade-id="${id}"]`,
	);
	if (!element) return;

	element.scrollIntoView({
		block: "center",
		inline: "nearest",
		behavior: "smooth",
	});
});

const handleMessage = (message: string): void => {
	toastMessage.value = translate(message);
	setTimeout((): void => {
		toastMessage.value = null;
	}, 1_000);
};
</script>

<style lang="scss" scoped>
.upgrade-box {
	display: flex;
	position: relative;
	flex-direction: column;
	gap: 0.5rem;
	box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
	border-width: 0.05rem;
	border-style: solid;
	border-image-slice: 1;
	width: 100%;
	max-width: 25rem;
	@include clickable-element;

	:deep(.upgrade-box__text) {
		z-index: 1;
		font-weight: 800;
		text-transform: uppercase;
	}
	&--light {
		box-shadow:
			0 0 0.5rem 0 rgba(153, 33, 203, 0.8),
			inset 0 0 0.5rem 0 rgba(49, 37, 22, 0.6);
		border-image-source: linear-gradient(to top, #836e49, #bfa370);
		background-image: linear-gradient(to top, #ab8849, #f2dcb5);

		span {
			color: #372615 !important;
			text-shadow: none !important;
		}

		&::before {
			opacity: 1 !important;
			// NOTE: Multiply blending is better for light colors
			mix-blend-mode: multiply !important;
		}

		:deep(.solid-bg-polygon) {
			fill: rgba(131, 110, 73, 0.8) !important;
		}

		:deep(.texture-bg-polygon) {
			opacity: 0.2 !important;
		}
		:deep(.upgrade-box__text) {
			color: #372615;
			text-shadow: none;
		}
		:deep(.price) {
			span {
				color: #372615 !important;
				text-shadow: none !important;
			}
		}
	}

	&--dark {
		border-image-source: linear-gradient(to top, #61667f, #787c8b);
		background-image: linear-gradient(to top, #3b3c48, #595d6d);
		:deep(.upgrade-box__text) {
			color: #f2dcb5;
		}
	}

	&--premium {
		box-shadow:
			0 0 1.25rem 0 rgba(153, 33, 203, 0.8),
			inset 0 0 0.5rem 0 rgba(49, 37, 22, 0.6);
	}

	&--event {
		:deep(.upgrade-box__text) {
			color: #f2dcb5;
			font-weight: 800;
			text-shadow: none;
		}
		&[data-event-type="christmas"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(189, 144, 50, 1) 0%,
				rgba(240, 211, 134, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(88, 8, 12, 1) 0%,
				rgba(152, 14, 20, 1) 100%
			);
		}
		&[data-event-type="easter"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(189, 144, 50, 1) 0%,
				rgba(240, 211, 134, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(52, 65, 4, 1) 0%,
				rgba(120, 149, 8, 1) 100%
			);
		}
		&[data-event-type="fishing"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(189, 144, 50, 1) 0%,
				rgba(240, 211, 134, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(8, 59, 78, 1) 0%,
				rgba(7, 98, 131, 1) 100%
			);
		}
		&[data-event-type="firefest"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(189, 144, 50, 1) 0%,
				rgba(240, 211, 134, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(111, 33, 15, 1) 0%,
				rgba(175, 64, 35, 1) 100%
			);
		}
		&[data-event-type="solarfair"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(189, 144, 50, 1) 0%,
				rgba(240, 211, 134, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(101, 60, 17, 1) 0%,
				rgba(196, 139, 59, 1) 100%
			);
			:deep(.texture-bg-polygon) {
				opacity: 0.1;
			}
			:deep(.upgrade-box__heading svg) {
				opacity: 0.8;
			}
		}
		&[data-event-type="hunting"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(189, 144, 50, 1) 0%,
				rgba(240, 211, 134, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(17, 63, 17, 1) 0%,
				rgba(35, 114, 35, 1) 100%
			);
		}
		&[data-event-type="halloween"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(189, 144, 50, 1) 0%,
				rgba(240, 211, 134, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(38, 27, 63, 1) 0%,
				rgba(114, 68, 221, 1) 100%
			);

			:deep(.solid-bg-polygon) {
				fill: rgba(26, 15, 50, 0.7);
			}
		}
		&[data-event-type="pirates"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(24, 66, 114, 1) 0%,
				rgba(34, 93, 158, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(10, 35, 64, 1) 0%,
				rgba(26, 71, 122, 1) 100%
			);

			:deep(.solid-bg-polygon) {
				fill: rgba(10, 35, 64, 0.7);
			}
		}
	}

	&--event-premium {
		border-image-source: linear-gradient(
			0deg,
			rgba(189, 144, 50, 1) 0%,
			rgba(240, 211, 134, 1) 100%
		);
		background-image: linear-gradient(
			0deg,
			rgba(91, 35, 121, 1) 0%,
			rgba(141, 81, 173, 1) 100%
		);
		:deep(.upgrade-box__text) {
			color: #fff3dd;
		}
	}

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		// NOTE: Color-burn blending is better for dark colors
		mix-blend-mode: color-burn;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat center;
		background-size: 40%;
		width: 100%;
		height: 100%;
		content: "";
	}

	&-container {
		@include flex-col-start;
		align-items: flex-start;
		height: 100%;

		.sub {
			width: 100%;
			height: 100%;
			@include flex-col-start;
			align-items: flex-start;
			gap: 0.5rem;

			.upgrade-box__content {
				padding-right: 0.625rem;
				padding-bottom: 0.75rem;

				&--has-flag {
					padding-right: 6.625rem;
				}

				&--has-price {
					padding-bottom: 0;
				}

				.description {
					display: -webkit-box;
					flex: 1 1 0;
					max-width: 12rem;
					color: #fff3dd;
					text-shadow: 0.044rem 0.044rem 0.0625rem rgba(0, 0, 0, 0.6);
				}
			}
		}

		&--row {
			@include flex-row-stretch; // IMPORTANT (upgrade-box__redirect)

			.description {
				-webkit-box-orient: vertical;
				overflow: hidden;
			}
		}
	}

	&__image {
		border-width: 0.125rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #bd9032, #f0d386);
		border-image-slice: 1;
		background: #e4e4e4;
		width: 2.25rem;
		height: 2.25rem;
	}

	&__content {
		@include flex-row-start;
		align-items: flex-start;
		gap: 0.5rem;
		z-index: 1;
		padding-right: 7rem;

		.description {
			display: -webkit-box;
			flex: 1 1 0;
			-webkit-box-orient: vertical;
			min-width: 0;
			overflow: hidden;
			color: #fff3dd;
			font-size: 0.8125rem;
			line-height: 1rem;
			user-select: none;
			text-shadow: 0.044rem 0.044rem 0.0625rem rgba(0, 0, 0, 0.6);
		}
	}

	&__heading {
		position: relative;
		flex-shrink: 0;
		gap: 0.5rem;
		height: 1.875rem;
		@include flex-row-start;
		padding-bottom: 0.375rem;
		overflow: hidden;
		&__wrapper {
			z-index: 1;
			@include flex-row-start;
			width: calc(100% - 6.25rem);
			max-width: 19rem;
			&__fw {
				width: calc(100% - 1rem);
			}
		}

		svg {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 0.6;
			z-index: 0;
			mix-blend-mode: multiply;
			width: 100%;
			height: 100%;
		}

		.ribbon-label {
			position: relative;
			z-index: 1;
		}

		.flag-with-star-icon {
			position: absolute;
			top: -0.125rem;
			right: 6.5rem;
			z-index: 1;
			background: url($path-icons + "upgrades/flag-with-star.avif")
				no-repeat center;
			background-size: contain;
			width: 1.875rem;
			height: 3.281rem;
		}

		span {
			position: relative;
			z-index: 1;
			padding: 0;
			color: #f2dcb5;
			font-weight: 800;
			font-size: 1rem;
			line-height: 1rem;
			user-select: none;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}

		:deep(.solid-bg-polygon) {
			fill: rgba(37, 37, 46, 0.8);
		}

		:deep(.texture-bg-polygon) {
			opacity: 0.4;
		}
	}

	.upgrade-box__heading,
	.upgrade-box__content,
	.upgrade-box__price {
		padding-left: 0.625rem;
		width: 100%;
	}

	.upgrade-box__price {
		@include flex-row-start;
		z-index: 1;
		margin-bottom: 0.5rem;

		span {
			color: #f2dcb5;
			font-weight: 800;
			line-height: 1rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}

		.maxed-out {
			display: flex;
			align-items: center;
			margin-left: -0.625rem;
			background-image: linear-gradient(
				to right,
				rgba(0, 93, 119, 0.8) 0%,
				rgba(36, 179, 219, 0.73) 40%,
				rgba(0, 93, 119, 0.4) 80%,
				transparent 100%
			);
			padding-bottom: 0.125rem;
			padding-left: 0.75rem;
			min-width: 10.0625rem;
			height: 1.5rem;
		}

		.amount {
			@include flex-row-center;
			gap: 0.25rem;
			font-size: 0.75rem;

			:deep(.price) {
				.icon {
					margin-top: 4%;
				}
			}
		}
	}

	.upgrade-box__redirect {
		@include flex-col-center;
		flex-shrink: 0;
		margin-top: -0.025rem;
		margin-right: -0.025rem;
		margin-bottom: -0.025rem;
		box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
		border-width: 0.075rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #bd9032, #f0d386);
		border-image-slice: 1;
		background-image: linear-gradient(to top, #4e1d61, #862cad);
		width: 3.125rem;
		// IMPORTANT: do not set height here, it is stretched
	}

	.upgrade-flag-wrapper {
		position: absolute;
		top: -0.125rem;
		right: 0.25rem;
		z-index: 1;
	}
	.upgrade-message {
		position: absolute;
		top: 50%;
		left: 40%;
	}
}
</style>
